import classNames from "classnames";
import { omit } from "lodash";
import { Button } from "PFComponents/button";
import Toggle from "PFComponents/toggle/toggle";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

import css from "./fields.less";

const DragItem = ({ id, label, config, setConfig }) => (
  <div className={css.dragItem}>
    <span className={css.dragHandle}>⸬</span>
    <Toggle
      compact
      checked={!config.hidden[id]}
      onChange={() => setConfig({ ...config, hidden: { ...config.hidden, [id]: !config.hidden[id] } })}
    />
    <span className={css.dragLabel}>{label}</span>
  </div>
);

DragItem.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  config: PropTypes.object.isRequired,
  setConfig: PropTypes.func.isRequired
};

const Fields = ({ config, setConfig, setOrder, defaultConfig }) => {
  const [items, setItems] = useState([]);
  const { order } = config;
  const fieldsRef = useRef();
  useEffect(() => {
    setItems(
      order.map((id) => {
        let content = null;
        const common = { config, setConfig };
        if (id === "summary") {
          content = <DragItem label="Summary" id={id} {...common} />;
        } else if (id === "experience") {
          content = <DragItem label="Experience" id={id} {...common} />;
        } else {
          const type = config.customTypes.find(({ name }) => name === id);
          if (type) {
            content = <DragItem label={type.display_as} id={id} {...common} />;
          }
        }

        return { id, content };
      })
    );
  }, [config, order]);

  const toggleAll = () => {
    const defaultHiddenKeys = Object.keys(defaultConfig.hidden);
    const set = new Set(Object.values(omit(config.hidden, ...defaultHiddenKeys)));

    if (set.size === 0 || (set.size === 1 && set.has(false))) {
      setConfig({
        ...config,
        hidden: { ...defaultConfig.hidden, ...order.reduce((mem, it) => ({ ...mem, [it]: true }), {}) }
      });
    } else {
      setConfig({ ...config, hidden: defaultConfig.hidden });
    }
  };

  return (
    <div>
      <Toggle
        inline
        label="Show Avatar"
        checked={!!config.showAvatar}
        onChange={(showAvatar) => setConfig({ ...config, showAvatar })}
      />

      <div className={css.toggleWrap}>
        <Button small onClick={toggleAll}>
          Toggle all
        </Button>
      </div>

      <DragDropContext
        onDragEnd={(result) => {
          if (!result.destination) {
            return;
          }

          const copy = [...order];
          const [item] = copy.splice(result.source.index, 1);
          copy.splice(result.destination.index, 0, item);
          setOrder(copy);
        }}
      >
        <div className={css.fieldsBox} ref={fieldsRef}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <>
                <div className={css.placeholderWrap}>{provided.placeholder}</div>

                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {items.map((item, index) => (
                    <Draggable key={item.id} draggableId={`${item.id}`} index={index}>
                      {(provided, snapshot) => {
                        const style = { ...provided.draggableProps.style };

                        if (snapshot.isDragging) {
                          // this mess is for supporting dragn end drop in a scrolled div
                          style.top -= fieldsRef.current.getBoundingClientRect().top;
                          style.top += fieldsRef.current.scrollTop;
                          style.left = 4;
                          if (style.transform) {
                            style.transform = style.transform.replace(/\d+px,/, "0,"); // only y-axis
                          }
                        }

                        return (
                          <div
                            className={classNames(css.draggableInner, { [css.dragged]: snapshot.isDragging })}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={style}
                          >
                            {item.content}
                          </div>
                        );
                      }}
                    </Draggable>
                  ))}
                </div>
              </>
            )}
          </Droppable>
        </div>
      </DragDropContext>
    </div>
  );
};

Fields.propTypes = {
  config: PropTypes.object.isRequired,
  setConfig: PropTypes.func.isRequired,
  setOrder: PropTypes.func.isRequired,
  defaultConfig: PropTypes.object.isRequired
};

export default Fields;
